import React, { type ComponentType, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Divider,
  FormLabel,
  Grid as GridBase,
  Icon,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import RpMuiSelect from "@/components/muiCustom/select/RpMuiSelect";
import CustomerList from "./CustomerList";
import EmailTemplate from "./EmailTemplate";
import RpDatePicker from "@/components/muiCustom/date/RpDatePicker";
import RpTimePicker from "@/components/muiCustom/date/RpTimePicker";
import { EMAIL_CAMPAIGN_CONST } from "@/features/campaigns/email/utils/emailCampaignConstants";
import { emailCampaignHelper } from "@/features/campaigns/email/utils/emailCampaignHelper";
import {
  isTemporarilyClosedLocation,
  TemporaryClosedStatusNote,
} from "@/components/muiCustom/note/InlineStatusNote";

type LocationId = string | number;

type EmailLocationData = {
  id: LocationId;
  name: string;
  [key: string]: unknown;
};

type EmailTemplateData = {
  id: LocationId;
  title: string;
  customer_list?: string;
  contact_count?: string | number;
  [key: string]: unknown;
};

type EmailLocationFormData = {
  template?: LocationId | "";
  schedule_type?: string;
  schedule_date?: string;
  schedule_time?: string;
  [key: string]: unknown;
};

type EmailLocationProps = {
  shouldExpandIt?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  setExpanded: React.Dispatch<React.SetStateAction<LocationId[]>>;
  loc: EmailLocationData;
  locFormData: EmailLocationFormData;
  setFieldValue: (_field: LocationId, _value: EmailLocationFormData) => void;
  templates: EmailTemplateData[];
};

type SelectOption = {
  label: string;
  value: LocationId;
};

type DateValue = {
  format: (_format: string) => string;
};

const Grid = GridBase as ComponentType<any>;
const EmailTemplatePreview = EmailTemplate as ComponentType<{
  data?: EmailTemplateData;
}>;
const DatePicker = RpDatePicker as ComponentType<{
  value: string | null;
  onChange: (_value: DateValue) => void;
}>;
const isTemporaryClosed = isTemporarilyClosedLocation as (
  _location: EmailLocationData
) => boolean;
const TemporaryClosedNote = TemporaryClosedStatusNote as ComponentType<{
  location: EmailLocationData;
}>;

function EmailLocation({
  shouldExpandIt,
  onBlur,
  onFocus,
  setExpanded,
  loc,
  locFormData,
  setFieldValue,
  templates,
}: EmailLocationProps) {
  const handleOnExpand =
    (panel: LocationId) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setExpanded((expanded) => [...expanded, panel]);
      } else {
        setExpanded((expanded) => expanded.filter((l) => l !== panel));
        onBlur?.();
      }
    };

  const isLocationFullyFilled = useMemo(() => {
    return emailCampaignHelper.isLocationHasValidSelection(locFormData);
  }, [locFormData]);

  const templateOptions = useMemo<SelectOption[]>(() => {
    return (
      templates?.map((t) => ({
        label: t.title,
        value: t.id,
      })) || []
    );
  }, [loc?.id, templates]);

  const scheduleOptions = useMemo<SelectOption[]>(() => {
    return [
      {
        label: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.label,
        value: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.value,
      },
      {
        label: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.label,
        value: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value,
      },
    ];
  }, [loc?.id]);

  const selectedTemplateData = useMemo(() => {
    return templates.find((t) => t.id === locFormData?.template);
  }, [templates, locFormData?.template]);

  const renderIcon = useMemo(() => {
    if (isLocationFullyFilled) {
      return "checked_circle";
    } else {
      return "location_mark";
    }
  }, [isLocationFullyFilled]);

  return (
    <div>
      <Accordion
        expanded={shouldExpandIt}
        onChange={handleOnExpand(loc.id)}
        className={`location mb-2`}
        onFocus={() => {
          onFocus?.();
        }}
        onBlur={() => {
          onBlur?.();
        }}
      >
        <AccordionSummary
          expandIcon={
            <ChevronRightIcon
              style={{
                transform: shouldExpandIt ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          }
          aria-controls={`panel${loc.id}-content`}
          id={`panel${loc.id}-header`}
          className="location-header"
        >
          <div>
            <Typography
              component="h3"
              variant="body1"
              className="d-flex align-items-center fw-sb"
            >
              <Icon
                component={RpSvgIcon}
                name={renderIcon}
                color="primary"
                className="mr-2"
              />
              {loc.name}
            </Typography>
            {isTemporaryClosed(loc) ? (
              <div className="mt-2 ml-4">
                <TemporaryClosedNote location={loc} />
              </div>
            ) : null}
          </div>
        </AccordionSummary>
        <AccordionDetails className="location-details">
          <Card className="w-100 border" elevation={0}>
            <CardContent>
              <Grid container spacing={1}>
                <Grid
                  item
                  size={{
                    xs: 12,
                    md: 6,
                    lg: 4,
                    xl: 4,
                  }}
                >
                  <FormLabel className="label text-dark">Template</FormLabel>
                  <RpMuiSelect
                    options={templateOptions}
                    value={locFormData.template ? [locFormData.template] : []}
                    onChange={(v: LocationId[]) => {
                      const newData = {
                        ...locFormData,
                        template: v?.[0] || "",
                      };
                      setFieldValue(loc.id, newData);
                    }}
                    multiple={false}
                  />
                </Grid>
              </Grid>

              <FormLabel className="label mt-3 mb-1 text-dark">
                Customer List
              </FormLabel>
              <div className="mb-4">
                <CustomerList
                  label={selectedTemplateData?.customer_list}
                  contactNo={selectedTemplateData?.contact_count}
                />
              </div>
              <EmailTemplatePreview data={selectedTemplateData} />
              <Divider className="my-3" />
              <Grid container spacing={1} justifyContent="space-between">
                <Grid
                  item
                  size={{
                    xs: 12,
                    md: 5,
                    lg: 3,
                    xl: 3,
                  }}
                >
                  <FormLabel className="label text-dark">Schedule</FormLabel>

                  <RpMuiSelect
                    value={
                      locFormData.schedule_type
                        ? [locFormData.schedule_type]
                        : []
                    }
                    onChange={(v: string[]) => {
                      const val = v?.[0] || "";
                      const isImmediate =
                        val === EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.value;

                      const newData = {
                        ...locFormData,
                        schedule_type: val,
                      };

                      if (isImmediate || !val) {
                        newData.schedule_date = "";
                        newData.schedule_time = "";
                      }

                      setFieldValue(loc.id, newData);
                    }}
                    options={scheduleOptions}
                    multiple={false}
                  />
                </Grid>
                <Grid
                  item
                  size={{
                    xs: 12,
                    md: 5,
                    lg: 3,
                    xl: 3,
                  }}
                ></Grid>
                {locFormData?.schedule_type ===
                EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value ? (
                  <>
                    <Grid
                      item
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                        xl: 3,
                      }}
                    >
                      <FormLabel className="label text-dark">
                        Start Date
                      </FormLabel>
                      <DatePicker
                        value={locFormData.schedule_date || null}
                        onChange={(v: DateValue) => {
                          const newData = {
                            ...locFormData,
                            schedule_date: v.format("YYYY-MM-DD") || "",
                          };
                          setFieldValue(loc.id, newData);
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                        xl: 3,
                      }}
                    >
                      <FormLabel className="label text-dark">
                        Set Time
                      </FormLabel>
                      <RpTimePicker
                        value={locFormData.schedule_time || null}
                        onChange={(v: string | null) => {
                          const newData = {
                            ...locFormData,
                            schedule_time: v || "",
                          };
                          setFieldValue(loc.id, newData);
                        }}
                      />
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default EmailLocation;
